import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import PageWithHeader from '../common/PageWithHeader';
import { generatePastMonths } from '../../utils/periodUtil';
import { useGetPEARSummaryQuery, PEARSummaryItem } from '../../redux/pearReviewApi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Grade colors
const gradeColors: Record<string, string> = {
  A: '#4caf50',
  B: '#8bc34a',
  C: '#ff9800',
  D: '#f44336',
};

const PIE_COLORS = ['#4caf50', '#8bc34a', '#ff9800', '#f44336'];

const PEARSummary: React.FC = () => {
  const monthOptions = generatePastMonths();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');

  // Query
  const { data: summaryData, isLoading } = useGetPEARSummaryQuery(
    selectedPeriod ? { reviewPeriod: selectedPeriod } : undefined
  );

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!summaryData || summaryData.length === 0) {
      return {
        totalEmployees: 0,
        averageScore: 0,
        gradeDistribution: [],
        topPerformers: [],
        needsImprovement: [],
      };
    }

    const totalEmployees = summaryData.length;
    const averageScore =
      summaryData.reduce((sum, item) => sum + (item.overallScore || 0), 0) / totalEmployees;

    // Grade distribution
    const gradeCounts = summaryData.reduce(
      (acc, item) => {
        const grade = item.finalGrade || 'D';
        acc[grade] = (acc[grade] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const gradeDistribution = Object.entries(gradeCounts).map(([grade, count]) => ({
      name: `Grade ${grade}`,
      value: count,
      grade,
    }));

    // Top performers (Grade A or B with score >= 100)
    const topPerformers = summaryData
      .filter((item) => item.overallScore >= 95)
      .sort((a, b) => b.overallScore - a.overallScore)
      .slice(0, 5);

    // Needs improvement (Grade D)
    const needsImprovement = summaryData
      .filter((item) => item.finalGrade === 'D')
      .sort((a, b) => a.overallScore - b.overallScore);

    return {
      totalEmployees,
      averageScore,
      gradeDistribution,
      topPerformers,
      needsImprovement,
    };
  }, [summaryData]);

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              PEAR Review Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Performance overview across the organization
            </Typography>
          </Box>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Review Period</InputLabel>
            <Select
              value={selectedPeriod}
              label="Review Period"
              onChange={(e: SelectChangeEvent) => setSelectedPeriod(e.target.value)}
            >
              <MenuItem value="">All Periods</MenuItem>
              {monthOptions.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.totalEmployees}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Employees Reviewed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color={
                        stats.averageScore >= 95
                          ? 'success.main'
                          : stats.averageScore >= 80
                          ? 'warning.main'
                          : 'error.main'
                      }
                    >
                      {stats.averageScore.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Team Average Score
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'success.light' }}>
                  <CardContent>
                    <Typography variant="h3" fontWeight="bold" color="success.contrastText">
                      {stats.topPerformers.length}
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                      Top Performers (95%+)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: stats.needsImprovement.length > 0 ? 'error.light' : 'grey.200' }}>
                  <CardContent>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color={stats.needsImprovement.length > 0 ? 'error.contrastText' : 'text.primary'}
                    >
                      {stats.needsImprovement.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={stats.needsImprovement.length > 0 ? 'error.contrastText' : 'text.secondary'}
                    >
                      Needs Improvement
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Grade Distribution Pie Chart */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: 350 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Grade Distribution
                  </Typography>
                  {stats.gradeDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={stats.gradeDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {stats.gradeDistribution.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={gradeColors[entry.grade] || PIE_COLORS[index % PIE_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                      <Typography color="text.secondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Score Distribution Bar Chart */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2, height: 350 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Employee Scores
                  </Typography>
                  {summaryData && summaryData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={summaryData.slice(0, 10)}
                        margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="employeeName"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={80}
                        />
                        <YAxis domain={[0, 120]} />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="overallScore"
                          name="Score (%)"
                          fill="#8884d8"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                      <Typography color="text.secondary">No data available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Summary Table */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Employee Performance Summary
              </Typography>
              <TableContainer>
                {summaryData && summaryData.length > 0 ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Review Period</TableCell>
                        <TableCell align="center">PEAR %</TableCell>
                        <TableCell align="center">Grade</TableCell>
                        <TableCell>Rating</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summaryData.map((item, index) => (
                        <TableRow key={item.employeeId} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.employeeName}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.designation || 'N/A'}</TableCell>
                          <TableCell>{item.reviewPeriod}</TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={
                                item.overallScore >= 95
                                  ? 'success.main'
                                  : item.overallScore >= 80
                                  ? 'warning.main'
                                  : 'error.main'
                              }
                            >
                              {item.overallScore.toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.finalGrade}
                              size="small"
                              sx={{
                                bgcolor: gradeColors[item.finalGrade] || '#9e9e9e',
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={
                                item.finalGrade === 'A'
                                  ? 'success.main'
                                  : item.finalGrade === 'D'
                                  ? 'error.main'
                                  : 'text.secondary'
                              }
                            >
                              {item.ratingDescription}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" fontWeight="bold">
                            TOTAL / AVERAGE
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {stats.averageScore.toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No approved PEAR Reviews found for the selected period
                    </Typography>
                  </Box>
                )}
              </TableContainer>
            </Paper>

            {/* Top Performers Section */}
            {stats.topPerformers.length > 0 && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="success.main">
                  Top Performers
                </Typography>
                <Grid container spacing={2}>
                  {stats.topPerformers.map((performer, index) => (
                    <Grid item xs={12} sm={6} md={4} key={performer.employeeId}>
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                #{index + 1} {performer.employeeName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {performer.designation}
                              </Typography>
                            </Box>
                            <Box textAlign="right">
                              <Typography variant="h5" fontWeight="bold" color="success.main">
                                {performer.overallScore.toFixed(1)}%
                              </Typography>
                              <Chip
                                label={performer.finalGrade}
                                size="small"
                                sx={{
                                  bgcolor: gradeColors[performer.finalGrade],
                                  color: 'white',
                                }}
                              />
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </>
        )}
      </Box>
    </PageWithHeader>
  );
};

export default PEARSummary;
